import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatErr } from "@/lib/api";
import { PRIORITIES } from "@/lib/constants";
import { fmtDateOnly, fmtDateTime, todayYMD } from "@/lib/format";
import { useAssignableOptions, useUsersLite } from "@/hooks/useData";
import { EmptyState, NSelect, PageHeader, Spinner } from "@/components/common";
import DatePicker from "@/components/DatePicker";

const ago = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

function ListBox({ title, items, setItems, testid }) {
  return (
    <section className="rounded-2xl border bg-card p-4" data-testid={testid}>
      <h3 className="mb-2 font-display font-bold">{title}</h3>
      {items.map((t, i) => (
        <div key={i} className="mb-2 flex gap-2">
          <Input value={t} onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))} />
          <Button variant="ghost" size="icon" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setItems([...items, ""])}><Plus className="mr-1 h-4 w-4" />Add</Button>
    </section>
  );
}

function PlanEditor({ plan, onDone }) {
  const qc = useQueryClient();
  const opts = useAssignableOptions();
  const { data: users = [] } = useUsersLite();
  const partners = users.filter((u) => u.is_partner && u.active).sort((a, b) => a.partner_order - b.partner_order);
  const [p, setP] = useState(plan);
  const [busy, setBusy] = useState(false);
  useEffect(() => setP(plan), [plan.id, plan.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const draft = p.status === "draft";
  const setItem = (id, k, v) => setP({ ...p, items: p.items.map((i) => (i.id === id ? { ...i, [k]: v } : i)) });
  const save = async (publish) => {
    setBusy(true);
    try {
      await api.put(`/plans/${p.id}`, { items: p.items.filter((i) => i.task.trim()), decisions: p.decisions.filter(Boolean), open_questions: p.open_questions.filter(Boolean) });
      if (publish) await api.post(`/plans/${p.id}/publish`);
      toast.success(publish ? "Published — checklist posted to chat and tasks created" : "Draft saved");
      qc.invalidateQueries({ queryKey: ["plans"] });
      if (publish) onDone();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };
  const groups = [...partners.map((u) => ({ id: u.id, name: u.name })), { id: null, name: "Unassigned" }];
  return (
    <div className="space-y-4" data-testid="plan-editor">
      {groups.map((g) => {
        const items = p.items.filter((i) => (i.partner_id || null) === g.id);
        if (!items.length && (!draft || !g.id)) return null;
        return (
          <section key={g.id || "none"} className="rounded-2xl border bg-card p-4" data-testid={`plan-group-${g.id || "none"}`}>
            <h3 className="mb-3 font-display font-bold">{g.name}</h3>
            {items.map((i) => (
              <div key={i.id} className="mb-3 grid gap-2 rounded-xl bg-secondary/40 p-3 md:grid-cols-[2fr_1fr_1fr_auto_auto]">
                <Input disabled={!draft} value={i.task} onChange={(e) => setItem(i.id, "task", e.target.value)} data-testid={`plan-item-task-${i.id}`} />
                <NSelect disabled={!draft} value={i.owner_id || ""} onChange={(v) => setItem(i.id, "owner_id", v)} placeholder="Owner" options={opts} testid={`plan-item-owner-${i.id}`} />
                <DatePicker value={i.deadline || ""} onChange={(v) => draft && setItem(i.id, "deadline", v)} testid={`plan-item-deadline-${i.id}`} />
                <NSelect disabled={!draft} value={i.priority} onChange={(v) => setItem(i.id, "priority", v)} options={PRIORITIES} />
                {draft && <Button variant="ghost" size="icon" onClick={() => setP({ ...p, items: p.items.filter((x) => x.id !== i.id) })} data-testid={`plan-item-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>}
                <div className="flex flex-wrap gap-2 text-xs md:col-span-5">
                  {draft && <NSelect value={i.partner_id || ""} onChange={(v) => setItem(i.id, "partner_id", v || null)} placeholder="Move to partner…" options={partners.map((u) => ({ value: u.id, label: u.name }))} className="h-8 w-48 text-xs" />}
                  {i.source_message_ids?.map((m, k) => <Link key={m} to={`/chat?m=${m}`} className="text-primary underline">Source {k + 1}</Link>)}
                </div>
              </div>
            ))}
            {draft && g.id && <Button size="sm" variant="outline" onClick={() => setP({ ...p, items: [...p.items, { id: `n${Date.now()}`, partner_id: g.id, owner_id: g.id, task: "", deadline: null, priority: "Medium", source_message_ids: [] }] })} data-testid={`plan-add-item-${g.id}`}><Plus className="mr-1 h-4 w-4" />Add item</Button>}
          </section>
        );
      })}
      <div className="grid gap-4 md:grid-cols-2">
        <ListBox title="Decisions taken" items={p.decisions} setItems={(v) => setP({ ...p, decisions: v })} testid="plan-decisions" />
        <ListBox title="Open questions / unresolved" items={p.open_questions} setItems={(v) => setP({ ...p, open_questions: v })} testid="plan-open-questions" />
      </div>
      {draft && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="plan-save-draft">Save draft</Button>
          <Button disabled={busy} onClick={() => save(true)} data-testid="plan-publish">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Publish</Button>
        </div>
      )}
    </div>
  );
}

export default function PlansPage() {
  const qc = useQueryClient();
  const [from, setFrom] = useState(ago(7));
  const [to, setTo] = useState(todayYMD());
  const [sel, setSel] = useState(null);
  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data,
    refetchInterval: (q) => (q.state.data?.some((p) => p.status === "generating") ? 3000 : false),
  });
  const current = plans.find((p) => p.id === sel) || plans[0];
  const gen = async () => {
    try {
      const { data } = await api.post("/plans/generate", { date_from: from, date_to: to });
      setSel(data.id);
      qc.invalidateQueries({ queryKey: ["plans"] });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const del = async (id) => {
    try { await api.delete(`/plans/${id}`); setSel(null); qc.invalidateQueries({ queryKey: ["plans"] }); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <>
      <PageHeader title="AI Weekly Action Plan" testid="plans-title" subtitle="Runs automatically every Monday 9:00 AM IST from the past 7 days of MOM chat. Edit the draft, then publish." />
      <div className="mb-6 flex flex-wrap items-end gap-2 rounded-2xl border bg-card p-4">
        <DatePicker value={from} onChange={setFrom} testid="plan-from" />
        <DatePicker value={to} onChange={setTo} testid="plan-to" />
        <Button onClick={gen} disabled={!from || !to} data-testid="plan-generate"><Sparkles className="mr-2 h-4 w-4" />Generate Action Plan</Button>
      </div>
      {isLoading ? <Spinner /> : !plans.length ? <EmptyState icon={Sparkles} title="No plans yet" testid="plans-empty" /> : (
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <div className="space-y-2" data-testid="plans-history">
            {plans.map((p) => (
              <button key={p.id} onClick={() => setSel(p.id)} data-testid={`plan-${p.id}`} className={`w-full rounded-xl border bg-card p-3 text-left ${current?.id === p.id ? "border-primary" : ""}`}>
                <p className="text-sm font-semibold">{fmtDateOnly(p.date_from)} → {fmtDateOnly(p.date_to)}</p>
                <p className="text-xs text-muted-foreground">{p.status}{p.completion != null ? ` · ${p.completion}% done` : ""} · {p.items.length} items</p>
              </button>
            ))}
          </div>
          <div>
            {current.status === "generating" && <p className="flex items-center gap-2 text-sm" data-testid="plan-generating"><Loader2 className="h-4 w-4 animate-spin" />Reading the chat and drafting the plan…</p>}
            {current.status === "failed" && <p className="text-sm text-destructive" data-testid="plan-failed">Generation failed: {current.error}</p>}
            {["draft", "published"].includes(current.status) && (
              <>
                <p className="mb-3 text-xs text-muted-foreground">{current.status === "published" ? `Published ${fmtDateTime(current.published_at)} · ${current.completion ?? 0}% complete` : "Draft — not yet visible to the team"}</p>
                <PlanEditor plan={current} onDone={() => {}} />
              </>
            )}
            {current.status !== "published" && current.status !== "generating" && <Button variant="ghost" className="mt-3 text-destructive" onClick={() => del(current.id)} data-testid="plan-delete"><Trash2 className="mr-1 h-4 w-4" />Delete</Button>}
          </div>
        </div>
      )}
    </>
  );
}
